import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Loader2,
  Mail,
  Phone,
  MessageSquare,
  Calendar,
  Building2,
  Pencil,
  Save,
  X,
  StickyNote,
  Clock,
} from "lucide-react";
import { crmGetSalesPerson, crmUpdateSalesPersonNotes } from "@/lib/crm.functions";
import { SafeAvatar } from "@/components/SafeAvatar";

export const Route = createFileRoute("/crm/sales/$userId")({
  component: SalesPersonDetail,
});

const CHANNEL_ICONS: Record<string, any> = {
  call: Phone,
  email: Mail,
  visit: Building2,
  whatsapp: MessageSquare,
  other: MessageSquare,
};

function SalesPersonDetail() {
  const { userId } = Route.useParams();
  const qc = useQueryClient();
  const getPerson = useServerFn(crmGetSalesPerson);
  const updateNotes = useServerFn(crmUpdateSalesPersonNotes);

  const q = useQuery({
    queryKey: ["crm", "sales-person", userId],
    queryFn: () => getPerson({ data: { user_id: userId } }),
  });

  const person = q.data?.person;
  const name = person?.display_name || person?.email || userId.slice(0, 8);
  const initial = (name || "?").charAt(0).toUpperCase();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (person && !editing) setDraft(person.admin_notes ?? "");
  }, [person?.admin_notes, editing, person]);

  const saveNotes = useMutation({
    mutationFn: () =>
      updateNotes({
        data: { user_id: userId, admin_notes: draft.trim() ? draft.trim() : null },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["crm", "sales-person", userId] });
      setEditing(false);
    },
  });

  const clientName = (id: string) =>
    q.data?.clients.find((c: any) => c.id === id)?.name ?? "Client";

  return (
    <div className="min-h-dvh bg-background pb-12">
      <header className="sticky top-0 z-20 rounded-2xl border border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-2 px-3 py-3 sm:px-4 lg:px-6">
          <Link
            to="/crm/sales"
            className="rounded-md p-2 text-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
            aria-label="Back to sales people"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-sm font-bold text-foreground sm:text-base">{name}</h1>
            {person?.email && (
              <p className="truncate text-xs text-muted-foreground">{person.email}</p>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-3 py-4 sm:px-4 sm:py-6 lg:px-6">
        {q.isLoading ? (
          <div
            className="flex items-center gap-2 text-sm text-muted-foreground"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
          </div>
        ) : q.error ? (
          <p className="text-sm text-destructive" role="alert">
            {(q.error as Error).message}
          </p>
        ) : (
          <>
            {/* Profile card */}
            <section
              aria-labelledby="profile-heading"
              className="rounded-2xl border border-border bg-card p-4 shadow-[0_2px_10px_-2px_rgba(16,24,40,0.05)] sm:p-5"
            >
              <h2 id="profile-heading" className="sr-only">
                Profile
              </h2>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <div
                  aria-hidden
                  className="relative grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-2xl bg-muted text-2xl font-bold text-foreground ring-4 ring-primary/10"
                >
                  <SafeAvatar
                    src={person?.avatar_signed_url}
                    name={name}
                    email={person?.email}
                    className="absolute inset-0 h-full w-full object-cover"
                    fallbackClassName="absolute inset-0 grid place-items-center bg-muted text-2xl font-bold text-foreground"
                    roundedClassName="rounded-2xl"
                  >
                    <span>{initial}</span>
                  </SafeAvatar>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-lg font-bold text-foreground">{name}</p>
                  {person?.email && (
                    <p className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-muted-foreground">
                      <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span className="truncate">{person.email}</span>
                    </p>
                  )}
                  {person?.home_city && (
                    <p className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-muted-foreground">
                      <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span className="truncate">{person.home_city}</span>
                    </p>
                  )}
                  <p className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    Last activity:{" "}
                    <span className="font-medium text-foreground">
                      {person?.last_active
                        ? new Date(person.last_active).toLocaleString()
                        : "—"}
                    </span>
                  </p>
                </div>
              </div>
            </section>

            {/* Notes card */}
            <section
              aria-labelledby="notes-heading"
              className="rounded-2xl border border-border bg-card p-4 shadow-[0_2px_10px_-2px_rgba(16,24,40,0.05)] sm:p-5"
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2
                  id="notes-heading"
                  className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-muted-foreground"
                >
                  <StickyNote className="h-4 w-4" aria-hidden /> Admin notes
                </h2>
                {!editing ? (
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="inline-flex min-h-9 items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
                    aria-label="Edit admin notes"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden /> Edit
                  </button>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        setDraft(person?.admin_notes ?? "");
                        setEditing(false);
                      }}
                      className="inline-flex min-h-9 items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
                      aria-label="Cancel editing"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden /> Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => saveNotes.mutate()}
                      disabled={saveNotes.isPending}
                      className="inline-flex min-h-9 items-center gap-1.5 rounded-md bg-[#0d6efd] px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-[#0b5ed7] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2 disabled:opacity-60"
                    >
                      {saveNotes.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                      ) : (
                        <Save className="h-3.5 w-3.5" aria-hidden />
                      )}
                      Save
                    </button>
                  </div>
                )}
              </div>
              {editing ? (
                <>
                  <label htmlFor="admin-notes" className="sr-only">
                    Admin notes
                  </label>
                  <textarea
                    id="admin-notes"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={5}
                    maxLength={4000}
                    placeholder="Private notes visible only to admins…"
                    className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
                  />
                  {saveNotes.error && (
                    <p className="mt-2 text-xs text-destructive" role="alert">
                      {(saveNotes.error as Error).message}
                    </p>
                  )}
                </>
              ) : person?.admin_notes ? (
                <p className="whitespace-pre-wrap text-sm text-foreground">
                  {person.admin_notes}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">No notes yet.</p>
              )}
            </section>

            {/* Clients */}
            <section aria-labelledby="clients-heading">
              <h2
                id="clients-heading"
                className="mb-3 text-sm font-bold uppercase tracking-wide text-muted-foreground"
              >
                Clients ({q.data!.clients.length})
              </h2>
              {q.data!.clients.length === 0 ? (
                <p className="text-sm text-muted-foreground">No clients yet.</p>
              ) : (
                <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {q.data!.clients.map((c: any) => (
                    <li key={c.id}>
                      <Link
                        to="/crm/$clientId"
                        params={{ clientId: c.id }}
                        className="block rounded-xl border border-border bg-card p-3 transition hover:border-[#0d6efd]/40 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
                        aria-label={`Open client ${c.name}`}
                      >
                        <p className="truncate font-semibold text-foreground">{c.name}</p>
                        <p className="text-xs capitalize text-muted-foreground">
                          {c.status} · {c.segment?.replace("_", " ") ?? "—"}
                        </p>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Activities */}
            <section aria-labelledby="activities-heading">
              <h2
                id="activities-heading"
                className="mb-3 text-sm font-bold uppercase tracking-wide text-muted-foreground"
              >
                Activities ({q.data!.activities.length})
              </h2>
              {q.data!.activities.length === 0 ? (
                <p className="text-sm text-muted-foreground">No activities logged.</p>
              ) : (
                <ul className="divide-y divide-border rounded-xl border border-border bg-card">
                  {q.data!.activities.map((a: any) => {
                    const Icon = CHANNEL_ICONS[a.channel] ?? MessageSquare;
                    return (
                      <li key={a.id} className="flex gap-3 p-3">
                        <Icon
                          className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                          aria-hidden
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                            <Link
                              to="/crm/$clientId"
                              params={{ clientId: a.client_id }}
                              className="truncate font-semibold text-foreground hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
                            >
                              {clientName(a.client_id)}
                            </Link>
                            <span className="flex shrink-0 items-center gap-1">
                              <Calendar className="h-3 w-3" aria-hidden />
                              <time dateTime={a.occurred_at}>
                                {new Date(a.occurred_at).toLocaleString()}
                              </time>
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-foreground">{a.summary}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
